/* eslint-disable max-classes-per-file */
import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'

import {
  View,
  Text,
  Image,
  ActivityIndicator,
  TouchableOpacity,
  StyleSheet,
} from 'react-native'

import { ImagePicker } from 'utils/uploads'
import {
  cameraPermissionsMessages,
  errorUploadingImageMessages,
  choosePhotoMessages,
  changePhotoMessages,
  getLocalizedText,
} from 'utils/languageLocale'
import { Alert } from 'utils/alerts'

import {
  uploadImage,
  changeValue,
  setDefaultValue,
  resetValue,
  getRawValue,
} from 'ducks/formInputs'

import BaseObject from '../BaseObject'

import Icon from './images/image-large.png'

// Base ImageUpload class with shared functionality
class BaseImageUpload extends BaseObject {
  static contextTypes = {
    getBaseURL: PropTypes.func,
    getImageUploadsBaseURL: PropTypes.func,
  }

  constructor(props) {
    super(props)

    this.handlePress = this.handlePress.bind(this)
  }

  handlePress() {
    const { object, uploadImage, resetValue } = this.props

    ImagePicker.showImagePicker(
      { quality: 0.7, includeBase64: true },
      response => {
        if (response.errorCode) {
          console.error('error:', response)
          if (response.errorCode === 'permission') {
            const { title, body } = getLocalizedText(cameraPermissionsMessages)
            return Alert.alert(title, body)
          } else {
            return Alert.alert('Error', response.errorMessage)
          }
        }

        // via: https://github.com/react-native-image-picker/react-native-image-picker#Asset-Object
        if (response.assets?.[0]) {
          response = response.assets?.[0]
        }

        if (!response.uri) {
          return
        }

        const previewURI = response.uri
        const data = response.base64 || response.data
        let filename = ''

        if (response.fileName) {
          filename = response.fileName
        } else if (response.filename) {
          filename = response.filename
        } else {
          // eslint-disable-next-line prefer-destructuring
          filename = previewURI.split('/').slice(-1)[0]
        }

        resetValue(object.id)

        const baseURL = this.context.getBaseURL()

        uploadImage(baseURL, object.id, filename, data, previewURI).catch(
          err => {
            console.error('ERROR:', err)
            const { title, body } = getLocalizedText(
              errorUploadingImageMessages
            )
            Alert.alert(title, body)
            resetValue(object.id)
          }
        )
      }
    )
  }

  getImageURL() {
    const { value } = this.props
    let imageURL = null

    if (value && value.preview) {
      imageURL = value.preview
    } else if (value && typeof value === 'object') {
      imageURL = `${this.context.getImageUploadsBaseURL()}/${value.url}?orient`
    } else if (typeof value === 'string') {
      imageURL = `${this.context.getImageUploadsBaseURL()}/${value}?orient`
    }

    return imageURL
  }

  getPending() {
    const { value } = this.props

    return value && value._pending
  }
}

// FullWidth variant component
class FullWidthUpload extends BaseImageUpload {
  static contextTypes = {
    getBaseURL: PropTypes.func,
    getImageUploadsBaseURL: PropTypes.func,
    getApp: PropTypes.func,
  }

  getBranding() {
    const { getApp } = this.context
    const app = getApp() || {}

    return app.branding
  }

  renderImage() {
    const pending = this.getPending()
    const imageURL = this.getImageURL()

    const { object } = this.props
    const { height } = object.attributes

    const textStyles = {
      ...this.textStyles(),
    }

    const innerWrapperStyles = {
      ...this.borderStyles(),
      ...this.shadowStyles(),
      ...this.backgroundStyles(),
      ...this.getBorderRadiusNew(),
    }

    if (imageURL) {
      return (
        <View>
          <View style={[styles.innerWrapper, innerWrapperStyles, { height }]}>
            <TouchableOpacity onPress={this.handlePress} style={{ flex: 1 }}>
              <View style={styles.preview}>
                <Image
                  style={[styles.previewImage, pending ? styles.faded : {}]}
                  source={{ uri: imageURL }}
                  resizeMode="contain"
                />
                {pending ? <ActivityIndicator color="#999999" /> : null}
              </View>
            </TouchableOpacity>
          </View>
          {pending ? null : (
            <TouchableOpacity
              onPress={this.handlePress}
              style={styles.selectAnother}
            >
              <Text style={[styles.selectImage, textStyles]}>
                {getLocalizedText(changePhotoMessages).body}
              </Text>
            </TouchableOpacity>
          )}
        </View>
      )
    }

    return (
      <View style={[styles.innerWrapper, innerWrapperStyles, { height }]}>
        <TouchableOpacity
          style={styles.emptyWrapper}
          onPress={this.handlePress}
        >
          <Text style={[styles.selectImage, textStyles]}>
            {getLocalizedText(choosePhotoMessages).body}
          </Text>
        </TouchableOpacity>
      </View>
    )
  }

  componentDidUpdate(prevProps) {
    const { value, setUploading } = this.props

    if (prevProps.value !== value) {
      if (
        (prevProps && prevProps.value && prevProps.value._pending) !==
        (this.props.value && value && value._pending)
      ) {
        if (setUploading) setUploading(!!value && value._pending)
      }
    }
  }

  render() {
    const {
      object: { layout },
    } = this.props

    return <View style={[styles.wrapper, layout]}>{this.renderImage()}</View>
  }
}

// Main ImageUpload component
export class ImageUploadWrapper extends BaseImageUpload {
  static contextTypes = {
    getBaseURL: PropTypes.func,
    getImageUploadsBaseURL: PropTypes.func,
  }

  componentWillUnmount() {
    const { object, resetValue } = this.props

    if (resetValue && object) {
      resetValue(object.id)
    }
  }

  renderImage() {
    const { setUploading } = this.props
    const pending = this.getPending()
    const imageURL = this.getImageURL()

    if (setUploading) {
      setUploading(pending)
    }

    if (imageURL) {
      return (
        <View style={styles.preview}>
          <Image
            style={[styles.previewImage, pending ? styles.faded : {}]}
            source={{ uri: imageURL }}
            resizeMode="contain"
          />
          {pending ? <ActivityIndicator color="#999999" /> : null}
        </View>
      )
    }

    return <Image source={Icon} style={styles.icon} />
  }

  render() {
    const { object, setUploading } = this.props
    const { attributes, layout } = object
    const { height } = attributes

    if (object.attributes.imageUploadType === 'fullWidth') {
      return <FullWidthUpload {...this.props} setUploading={setUploading} />
    }

    const { opacity } = this.getOpacity()

    return (
      <View style={layout}>
        <TouchableOpacity onPress={this.handlePress}>
          <View
            style={[
              styles.wrapper,
              { height, opacity, ...this.getBorderRadiusNew() },
            ]}
          >
            {this.renderImage()}
          </View>
        </TouchableOpacity>
      </View>
    )
  }
}

// Connection utilities
export const connectImageUpload = component => {
  const mapStateToProps = (state, { object }) => ({
    value: getRawValue(state, object.id),
  })

  const ConnectedImageUpload = connect(mapStateToProps, {
    uploadImage,
    changeValue,
    setDefaultValue,
    resetValue,
  })(component)

  class WrappedImageUpload extends Component {
    static contextTypes = {
      getBindingsList: PropTypes.func,
    }

    render() {
      const { getBindingsList } = this.context

      return (
        <ConnectedImageUpload
          {...this.props}
          getBindingsList={getBindingsList}
        />
      )
    }
  }

  return WrappedImageUpload
}

const styles = StyleSheet.create({
  wrapper: {
    backgroundColor: '#eee',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
  },
  innerWrapper: {
    overflow: 'hidden',
  },
  preview: {
    position: 'absolute',
    left: 0,
    top: 0,
    width: '100%',
    height: '100%',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#000',
  },
  previewImage: {
    position: 'absolute',
    left: 0,
    top: 0,
    width: '100%',
    height: '100%',
  },
  faded: {
    opacity: 0.5,
  },
  icon: {
    opacity: 0.2,
    width: 36,
    height: 36,
  },
  emptyWrapper: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  selectImage: {
    color: '#06f',
    fontWeight: '500',
    fontSize: 14,
  },
  selectAnother: {
    marginTop: 8,
    alignItems: 'center',
  },
})

export default connectImageUpload(ImageUploadWrapper)
